import { skipToken } from '@apollo/client/react'
import { type ReactNode, useContext, useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import FluentSpinnerIos20Filled from '~icons/fluent/spinner-ios-20-filled'
import { UILink } from 'src/components/ui/Link'
import Text from 'src/components/ui/Text'
import { useCommentsRedesignExperiment } from 'src/experiments/experiments/2605/commentsRedesign'
import { type Message } from 'src/services/apollo/generated/graphql'
import { useGetMessage } from 'src/services/apollo/message'
import { useMe } from 'src/services/helpers/user'
import { getCommentTextModerationInfo } from 'src/utils/comment'
import { cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import { createProfileUrl, getUserDisplayName } from 'src/utils/user'
import { CommentContent } from './CommentContent'
import { CommentContext } from './context'
import { useScrollToComment } from './scrollToComment'

function useReplyMessage(messageId: string) {
  const ctx = useContext(CommentContext)
  const hasCache = ctx?.messageCache.has(messageId) ?? false
  const { data, loading } = useGetMessage(hasCache ? skipToken : messageId)
  const message = data ?? ctx?.messageCache.get(messageId)
  return { message, loading, hasCache }
}

export function NulledComment({
  displayText,
  hoverText,
}: {
  displayText: ReactNode
  hoverText?: ReactNode
}) {
  const [hovered, setHovered] = useState(false)
  return (
    <span
      className={hovered ? 'opacity-50' : 'uppercase opacity-50'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered && hoverText ? hoverText : displayText}
    </span>
  )
}

function RepliedCommentContainer({
  className,
  style,
  children,
}: StyleProps & { children: ReactNode }) {
  return (
    <div
      className={cx(
        'pl-2 border-l-2 border-black/50 dark:border-white/50',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  )
}

function TreatmentQuoteBox({
  parent,
  className,
  style,
  children,
}: StyleProps & {
  parent: Pick<Message, 'id' | 'topicId' | 'createdAt'>
  children: ReactNode
}) {
  const { onPress, loading } = useScrollToComment(parent)
  return (
    <AriaButton
      onPress={onPress}
      isDisabled={loading}
      className={cx(
        'flex gap-3 w-fit max-w-full text-left rounded-md py-2 pl-2 pr-3',
        loading && 'opacity-60',
        className,
      )}
      style={style}
    >
      <span className="flex-shrink-0 w-0.5 self-stretch bg-neutral-300 dark:bg-neutral-500 rounded-full" />
      <span className="flex-1 min-w-0">{children}</span>
      {loading && (
        <FluentSpinnerIos20Filled className="flex-shrink-0 self-center size-4 animate-spin" />
      )}
    </AriaButton>
  )
}

export function RepliedComment({
  messageId,
  className,
  style,
}: StyleProps & { messageId: string }) {
  const { me } = useMe()
  const { isTreatment } = useCommentsRedesignExperiment()
  const { message, loading, hasCache } = useReplyMessage(messageId)

  const { isFlagged } = getCommentTextModerationInfo(message?.extra)

  if (isTreatment) {
    const inner =
      !hasCache && loading ? (
        <FluentSpinnerIos20Filled className="size-5 animate-spin" />
      ) : !message ? (
        <NulledComment
          displayText={<Text i18nKey="comment:notice.delete.success" />}
        />
      ) : isFlagged ? (
        <NulledComment
          displayText={<Text i18nKey="comment:content.flagged-hidden" />}
          hoverText={me ? message.content : undefined}
        />
      ) : (
        <CommentContent
          className="line-clamp-3 text-neutral-500"
          content={message.content}
        />
      )

    if (!message) {
      return (
        <div
          className={cx(
            'flex gap-3 w-fit max-w-full rounded-md py-2 pl-2 pr-3',
            className,
          )}
          style={style}
        >
          <span className="flex-shrink-0 w-0.5 self-stretch bg-neutral-300 dark:bg-neutral-500 rounded-full" />
          <span className="flex-1 min-w-0">{inner}</span>
        </div>
      )
    }

    return (
      <TreatmentQuoteBox parent={message} className={className} style={style}>
        {inner}
      </TreatmentQuoteBox>
    )
  }

  const containerProps = { className, style }

  if (!hasCache && loading) {
    return (
      <RepliedCommentContainer {...containerProps}>
        <FluentSpinnerIos20Filled className="size-5 animate-spin" />
      </RepliedCommentContainer>
    )
  }
  if (!message) {
    return (
      <RepliedCommentContainer {...containerProps}>
        <NulledComment
          displayText={<Text i18nKey="comment:notice.delete.success" />}
        />
      </RepliedCommentContainer>
    )
  }
  if (isFlagged) {
    return (
      <RepliedCommentContainer {...containerProps}>
        <NulledComment
          displayText={<Text i18nKey="comment:content.flagged-hidden" />}
          hoverText={me ? message.content : undefined}
        />
      </RepliedCommentContainer>
    )
  }

  return (
    <RepliedCommentContainer {...containerProps}>
      <p className="text-neutral-500">
        <Text i18nKey="comment:reply.reply-to.label" />{' '}
        <UILink
          href={message.author ? createProfileUrl(message.author) : undefined}
          className="px-1 -mx-1 font-semibold rounded"
          onPress={() => track('user_link_click')}
        >
          {getUserDisplayName(message.author)}
        </UILink>
      </p>
      <CommentContent
        className="line-clamp-3 text-neutral-600 dark:text-neutral-400"
        content={message.content}
      />
    </RepliedCommentContainer>
  )
}

export function ReplyTargetInlineLabel({
  messageId,
  className,
  style,
}: StyleProps & { messageId: string }) {
  const { message } = useReplyMessage(messageId)
  const { onPress, loading } = useScrollToComment(message ?? undefined)

  if (!message) return null

  return (
    <span
      className={cx('block text-neutral-500 truncate', className)}
      style={style}
    >
      <Text
        i18nKey="comment:reply.in-reply-to"
        defaults="in reply to <0>{{name}}</0>"
        values={{ name: getUserDisplayName(message.author) }}
        components={[
          <AriaButton
            key="0"
            onPress={onPress}
            isDisabled={loading}
            className={cx(
              'font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100',
              loading && 'opacity-60',
            )}
          />,
        ]}
      />
    </span>
  )
}
